import React from 'react'

import { Icon } from '@clientbase/clientbase-library'
import { Box, Typography } from '@mui/material'

import { theme } from 'styles/theme'

import { TimelineRecord, TimelineStatistics } from '../../types/invoiceTimeline'
import { buildTimelineSummary, TimelineFilter } from './TimelineSummary.utils'

interface TimelineSummaryProps {
  records?: TimelineRecord[]
  statistics?: TimelineStatistics
  activeFilter?: TimelineFilter | null
  onFilterChange?: (filter: TimelineFilter | null) => void
}

/**
 * SC-10890: os três números da cobrança acima do trilho — enviadas, falhas e
 * aberturas — que também filtram a timeline.
 *
 * Respondem de relance o que hoje só se obtém contando linhas numa lista que
 * pode ter 24 envios. É a leitura que abre a conversa de cobrança: "mandei 24 e
 * ele nunca abriu" pede uma abordagem, "2 falharam" pede conferir o contato, e
 * "abriu 6 vezes e não pagou" pede outra.
 *
 * Clicar filtra o trilho (só no front, sobre o que já está carregado) e o card
 * ativo ganha um X para limpar. Card com zero não é clicável: filtrar para uma
 * lista vazia é um beco sem saída, e o estado vazio não explicaria melhor do que
 * o próprio zero já explica.
 *
 * Flat, borda fina e peso no número, sem tamanho grande: pelo DESIGN.md, número
 * graúdo é vocabulário de dashboard, e isto vive dentro de um accordion de modal.
 * A cor entra só onde muda a decisão — falha em error-mid, e apenas quando existe
 * falha. Zero falhas fica neutro, porque um zero vermelho alarma sem motivo.
 */
const TimelineSummary = ({
  records,
  statistics,
  activeFilter = null,
  onFilterChange
}: TimelineSummaryProps) => {
  const summary = buildTimelineSummary(records, statistics)

  // Abertura legada: a cobrança sabe QUANTAS vezes foi aberta (openCount) sem
  // ter um evento por abertura. Filtrar por ela mostraria uma lista vazia
  // contradizendo o número do card, então esse card não filtra.
  const hasOpenRecords = (records ?? []).some((r) => r.kind === 'open')

  // A escala `neutral` do tema é INVERTIDA (800 = #F2F5F2, quase branco). O
  // número é o dado principal do card e vai no tom mais escuro; o rótulo abaixo
  // é que recua.
  const cards: {
    key: TimelineFilter
    label: string
    value: number
    color: string
    filterable: boolean
  }[] = [
    {
      key: 'sent',
      label:
        summary.sent === 1 ? 'Notificação enviada' : 'Notificações enviadas',
      value: summary.sent,
      color: theme.palette.neutral[100],
      filterable: summary.sent > 0
    },
    {
      key: 'failed',
      label: summary.failed === 1 ? 'Falha no envio' : 'Falhas no envio',
      value: summary.failed,
      // Zero falha não merece alarme; uma falha merece.
      color: summary.failed > 0 ? '#990017' : theme.palette.neutral[100],
      filterable: summary.failed > 0
    },
    {
      key: 'opened',
      label:
        summary.opened === 1 ? 'Abertura da fatura' : 'Aberturas da fatura',
      value: summary.opened,
      color: summary.opened > 0 ? '#1A803C' : theme.palette.neutral[100],
      filterable: summary.opened > 0 && hasOpenRecords
    }
  ]

  return (
    <Box
      display="grid"
      gridTemplateColumns="repeat(3, 1fr)"
      gap="8px"
      marginBottom="20px"
      sx={{
        // Duas colunas antes de uma: em 620px (onde a timeline também colapsa)
        // cada card ganha ~156px de texto útil, e "Notificações enviadas" pede
        // ~131px. Em três colunas essa faixa daria 99px e o rótulo quebraria em
        // duas ou três linhas, desalinhando a altura dos cartões vizinhos.
        [theme.breakpoints.down(620)]: {
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
        },
        // Abaixo de 420px nem duas cabem: 2 colunas dariam ~78px de texto útil.
        [theme.breakpoints.down(420)]: { gridTemplateColumns: 'minmax(0, 1fr)' }
      }}
    >
      {cards.map((card) => {
        const active = activeFilter === card.key
        const clickable = card.filterable && !!onFilterChange

        return (
          <Box
            key={card.key}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            aria-pressed={clickable ? active : undefined}
            aria-label={
              clickable
                ? `${card.label}: ${card.value}. ${
                    active ? 'Remover filtro' : 'Filtrar a lista'
                  }`
                : undefined
            }
            onClick={
              clickable
                ? () => onFilterChange?.(active ? null : card.key)
                : undefined
            }
            onKeyDown={(event) => {
              if (!clickable) return
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onFilterChange?.(active ? null : card.key)
              }
            }}
            padding="10px 12px"
            borderRadius="8px"
            position="relative"
            minWidth={0}
            sx={{
              // Ativo se distingue por borda e superfície, não por sombra
              // (Flat-By-Default) nem por fundo colorido, que competiria com a
              // cor do próprio número.
              border: `1px solid ${
                active ? theme.palette.neutral[400] : '#E3E6E3'
              }`,
              backgroundColor: active ? theme.palette.neutral[900] : '#FFFFFF',
              cursor: clickable ? 'pointer' : 'default',
              transition: 'border-color 120ms, background-color 120ms',
              '&:hover': clickable
                ? { borderColor: theme.palette.neutral[500] }
                : undefined
            }}
          >
            <Typography
              variant="mdBold"
              sx={{
                color: card.color,
                // Abre calha para o X quando o card está ativo, senão em coluna
                // estreita o número encosta no ícone.
                paddingRight: active ? '22px' : 0
              }}
            >
              {card.value}
            </Typography>
            <Typography
              variant="xsLight"
              sx={{
                color: theme.palette.neutral[300],
                display: 'block',
                // Rótulo longo quebra em linha em vez de alargar a coluna: sem
                // isto o grid estoura e o modal ganha rolagem horizontal.
                overflowWrap: 'anywhere'
              }}
            >
              {card.label}
            </Typography>

            {active && (
              <Box
                position="absolute"
                top="6px"
                right="6px"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                // Alvo de toque de 24px: o ícone sozinho tem 16px, abaixo do
                // mínimo confortável no celular. O card inteiro também limpa o
                // filtro, então isto é reforço visual, não o único caminho.
                width="24px"
                height="24px"
                sx={{ color: theme.palette.neutral[300] }}
              >
                <Icon icon="close" fontSize="small" />
              </Box>
            )}
          </Box>
        )
      })}
    </Box>
  )
}

export default TimelineSummary
